import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Receipt, X, CheckCircle2 } from "lucide-react";
import { api, idr } from "../../lib/api";
import { formatTanggal, hariIniISO } from "../../lib/tanggal";

/** Pengembalian ke Pelanggan — nota DOWNGRADE (batch N3, brief 22 Sep 2026).
 *  Nilai trade-in melebihi belanja: selisihnya ditransfer Finance ke rekening
 *  pelanggan (jawaban owner 29 Agu), bukan kembalian dari laci toko. Nota
 *  yang lahir dari layar N3 dijurnal K 2170 saat approve; panel ini sisi
 *  pelunasannya (`routers/finance/kewajiban_pelanggan.py`, D 2170 / K kas-bank).
 *
 *  Tombol Bayar memakai `aksi_saya` per baris dari backend (izin
 *  finance_utang.update + ambang Finance Staff — aturan yang SAMA dengan
 *  endpoint bayar), daftar akun sumber juga dari backend (hanya kas/bank,
 *  tanpa penampung trade-in 1055), dan "ganti rekening" hanya ditawarkan bila
 *  `boleh_ganti_rekening`. Layar tidak menyalin aturan itu sendiri.
 *  Nota yang rekeningnya belum lengkap (`rekening_lengkap` false — data anomali
 *  sejak tinjauan 1) membuka kolom rekening + sumbernya WAJIB, bukan "ganti".
 *  Riwayat "sudah dibayar" dibatasi server (`dibayar_terpotong`); yang
 *  menunggu tidak pernah dipotong.
 *  Panel hilang sendiri kalau kosong / tak berizin. */
export default function PanelKewajibanPelanggan() {
  const [data, setData] = useState(null);
  const [muat, setMuat] = useState(0);
  const [tampil, setTampil] = useState("aktif");
  const [bayar, setBayar] = useState(null);

  useEffect(() => {
    let batal = false;
    api.get("/finance/kewajiban-pelanggan")
      .then(r => { if (!batal) setData(r.data); })
      // 403 / galat: panel hilang, sisa halaman Utang tetap utuh.
      .catch(() => { if (!batal) setData(null); });
    return () => { batal = true; };
  }, [muat]);

  const semua = data?.items || [];
  if (semua.length === 0) return null;
  const aktif = semua.filter(x => x.status !== "dibayar");
  const items = tampil === "aktif" ? aktif : semua.filter(x => x.status === "dibayar");

  return (
    <div className="bg-white border border-sky-200 rounded-xl shadow-sm" data-testid="fin-kewajiban-pelanggan">
      <div className="px-4 py-3 border-b border-sky-100 bg-sky-50/60 flex items-center gap-2 flex-wrap">
        <Receipt className="w-4 h-4 text-sky-700" />
        <p className="text-sm font-semibold text-slate-900">Pengembalian ke Pelanggan (nota downgrade)</p>
        <span className="text-xs text-slate-500">
          {aktif.length} menunggu · {idr(data?.total_menunggu || 0)}
        </span>
        <div className="ml-auto inline-flex gap-1">
          {[["aktif", "Menunggu"], ["dibayar", "Sudah dibayar"]].map(([k, l]) => (
            <button key={k} onClick={() => setTampil(k)} data-testid={`fin-kewajiban-tab-${k}`}
              className={`px-2.5 h-7 rounded text-xs font-medium ${tampil === k ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
              {l}
            </button>
          ))}
        </div>
        <p className="w-full text-[11px] text-slate-500">
          Nilai trade-in melebihi belanja — selisihnya ditransfer ke rekening pelanggan yang dicatat di nota.
          Tercatat di akun {data?.akun_kewajiban?.kode || "2170"} sejak nota disetujui kasir.
        </p>
      </div>
      <div className="divide-y divide-slate-100">
        {items.length === 0 && <p className="px-4 py-3 text-xs text-slate-500">Tidak ada.</p>}
        {tampil === "dibayar" && data?.dibayar_terpotong && (
          <p className="px-4 py-2 text-[11px] text-slate-500" data-testid="fin-kewajiban-dibayar-terpotong">
            Menampilkan {items.length} pembayaran terbaru dari {data.dibayar_total} — yang lebih lama
            tetap tercatat di Buku Besar akun {data?.akun_kewajiban?.kode || "2170"}.
          </p>
        )}
        {items.map(x => (
          <div key={x.sale_id} className="px-4 py-3 text-sm flex items-start justify-between gap-3 flex-wrap"
            data-testid={`fin-kewajiban-${x.sale_id}`}>
            <div className="min-w-0">
              <p className="font-mono text-[11px] text-slate-500">
                Nota {x.kode_nota} · {formatTanggal(x.tanggal)} · {x.cabang || "-"}
                {x.status === "perlu_dicek" && (
                  <span className="ml-2 px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-sans text-[10px]">perlu dicek</span>
                )}
              </p>
              <p className="text-slate-900">{x.nama_pelanggan || "-"}{x.no_hp ? ` · ${x.no_hp}` : ""}</p>
              <p className="text-[11px] text-slate-500">
                Rekening: {[x.rekening?.nama_bank, x.rekening?.no_rekening].filter(Boolean).join(" ") || "-"}
                {x.rekening?.atas_nama ? ` a.n. ${x.rekening.atas_nama}` : ""}
              </p>
              {x.alasan && <p className="text-[11px] text-amber-700">{x.alasan}</p>}
              {x.bayar && (
                <p className="text-[11px] text-emerald-700">
                  Ditransfer {formatTanggal(x.bayar.tanggal)} dari {x.bayar.akun_nama || "-"} oleh {x.bayar.oleh || "-"}
                </p>
              )}
            </div>
            <div className="text-right">
              <p className="font-mono text-slate-900">{idr(x.nominal)}</p>
              {x.aksi_saya?.bayar && (
                <button onClick={() => setBayar(x)} data-testid={`fin-kewajiban-bayar-${x.sale_id}`}
                  className="mt-1 h-8 px-3 rounded-lg bg-sky-600 hover:bg-sky-700 text-white text-xs font-medium inline-flex items-center gap-1.5">
                  <CheckCircle2 className="w-3.5 h-3.5" /> Catat transfer
                </button>
              )}
              {x.aksi_saya?.alasan && (
                <p className="text-[11px] text-amber-700 mt-1" data-testid={`fin-kewajiban-alasan-${x.sale_id}`}>
                  {x.aksi_saya.alasan}
                </p>
              )}
            </div>
          </div>
        ))}
      </div>
      {bayar && (
        <FormBayarKewajiban baris={bayar} akunSumber={data?.akun_sumber || []}
          bolehGantiRekening={!!data?.boleh_ganti_rekening}
          onClose={(berhasil) => { setBayar(null); if (berhasil) setMuat(n => n + 1); }} />
      )}
    </div>
  );
}

function FormBayarKewajiban({ baris, akunSumber, bolehGantiRekening, onClose }) {
  const [akunId, setAkunId] = useState("");
  const [tanggal, setTanggal] = useState(hariIniISO());
  // Rekening belum lengkap di nota: kolom rekening WAJIB tampil (pengisian,
  // bukan penggantian) — tanpa rekening endpoint bayar selalu menolak.
  const isiRekening = baris.rekening_lengkap === false;
  const [gantiPilih, setGanti] = useState(false);
  const ganti = isiRekening || gantiPilih;
  const [rek, setRek] = useState({ nama_bank: "", no_rekening: "", atas_nama: "", alasan_rekening: "" });
  const [catatan, setCatatan] = useState("");
  const [galat, setGalat] = useState("");
  const [kirimSedang, setKirimSedang] = useState(false);

  const kirim = async () => {
    setGalat("");
    if (!akunId) { setGalat("Pilih akun kas/bank tempat transfer keluar."); return; }
    // Cermin server (tinjauan 2): transfer tidak boleh bertanggal sebelum nota —
    // kewajibannya baru lahir di tanggal nota.
    const tglNota = (baris.tanggal || "").slice(0, 10);
    if (tglNota && tanggal < tglNota) {
      setGalat(`Tanggal bayar ${tanggal} lebih awal dari tanggal nota ${tglNota} — pengembalian tidak bisa `
        + "dicatat sebelum kewajibannya lahir.");
      return;
    }
    const body = { akun_id: akunId, tanggal, catatan: catatan.trim() || null };
    if (ganti) {
      if (!rek.alasan_rekening.trim()) {
        setGalat(isiRekening ? "Tulis sumber rekening ini (mis. dikonfirmasi telepon ke pelanggan)."
          : "Tulis alasan memakai rekening lain.");
        return;
      }
      Object.assign(body, {
        nama_bank: rek.nama_bank.trim() || null, no_rekening: rek.no_rekening.trim() || null,
        atas_nama: rek.atas_nama.trim() || null, alasan_rekening: rek.alasan_rekening.trim(),
      });
    }
    setKirimSedang(true);
    try {
      const r = await api.post(`/finance/kewajiban-pelanggan/${baris.sale_id}/bayar`, body);
      toast.success(r.data?.message || "Tercatat");
      onClose(true);
    } catch (e) {
      setGalat(e?.response?.data?.detail || "Gagal mencatat pengembalian");
    } finally { setKirimSedang(false); }
  };

  const inp = "w-full h-9 px-2 rounded border border-slate-200 text-sm";
  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={() => onClose(false)}>
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl" onClick={e => e.stopPropagation()}
        data-testid="fin-kewajiban-modal">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-semibold text-sm">Catat transfer ke {baris.nama_pelanggan || "pelanggan"}</h3>
          <button onClick={() => onClose(false)} className="p-1.5 hover:bg-slate-100 rounded"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-3 text-sm">
          <p>Nominal <span className="font-mono font-semibold">{idr(baris.nominal)}</span> (dari jurnal nota {baris.kode_nota})</p>
          <Kolom label="Keluar dari akun *">
            <select value={akunId} onChange={e => setAkunId(e.target.value)} className={inp + " bg-white"}
              data-testid="fin-kewajiban-akun">
              <option value="">— pilih akun kas/bank —</option>
              {akunSumber.map(a => <option key={a.id} value={a.id}>{a.code} · {a.name}</option>)}
            </select>
          </Kolom>
          <Kolom label="Tanggal transfer">
            <input type="date" value={tanggal} onChange={e => setTanggal(e.target.value)} className={inp}
              min={(baris.tanggal || "").slice(0, 10) || undefined}
              data-testid="fin-kewajiban-tanggal" />
          </Kolom>
          <div className="text-xs bg-slate-50 border border-slate-200 rounded p-2">
            Rekening di nota: {[baris.rekening?.nama_bank, baris.rekening?.no_rekening].filter(Boolean).join(" ") || "-"}
            {baris.rekening?.atas_nama ? ` a.n. ${baris.rekening.atas_nama}` : ""}
          </div>
          {isiRekening ? (
            <p className="text-xs text-amber-800" data-testid="fin-kewajiban-isi-rekening">
              Rekening pelanggan belum tercatat di nota — isi rekening tujuan dan sumbernya.
            </p>
          ) : bolehGantiRekening && (
            <label className="inline-flex items-center gap-2 text-xs">
              <input type="checkbox" checked={gantiPilih} onChange={e => setGanti(e.target.checked)}
                data-testid="fin-kewajiban-ganti-rekening" /> Transfer ke rekening lain
            </label>
          )}
          {ganti && (
            <div className="grid grid-cols-1 gap-2">
              <input value={rek.nama_bank} onChange={e => setRek(r => ({ ...r, nama_bank: e.target.value }))}
                placeholder="Nama bank" className={inp} data-testid="fin-kewajiban-bank" />
              <input value={rek.no_rekening} onChange={e => setRek(r => ({ ...r, no_rekening: e.target.value }))}
                placeholder="No. rekening" className={inp} data-testid="fin-kewajiban-norek" />
              <input value={rek.atas_nama} onChange={e => setRek(r => ({ ...r, atas_nama: e.target.value }))}
                placeholder="Atas nama" className={inp} data-testid="fin-kewajiban-atasnama" />
              <input value={rek.alasan_rekening} onChange={e => setRek(r => ({ ...r, alasan_rekening: e.target.value }))}
                placeholder={isiRekening ? "Sumber rekening ini *" : "Alasan memakai rekening lain *"}
                className={inp} data-testid="fin-kewajiban-alasan-rekening" />
            </div>
          )}
          <Kolom label="Catatan">
            <input value={catatan} onChange={e => setCatatan(e.target.value)} className={inp}
              placeholder="opsional (mis. no. referensi transfer)" data-testid="fin-kewajiban-catatan" />
          </Kolom>
          {galat && (
            <p className="text-xs text-rose-800 bg-rose-50 border border-rose-200 rounded px-2 py-1.5" role="alert"
              data-testid="fin-kewajiban-galat">{galat}</p>
          )}
        </div>
        <div className="border-t border-slate-100 p-4 flex justify-end gap-2">
          <button onClick={() => onClose(false)} className="h-9 px-4 rounded-lg border border-slate-200 text-sm">Batal</button>
          <button onClick={kirim} disabled={kirimSedang} data-testid="fin-kewajiban-kirim"
            className="h-9 px-4 rounded-lg bg-sky-600 hover:bg-sky-700 text-white text-sm font-medium disabled:opacity-50">
            {kirimSedang ? "Menyimpan…" : "Catat transfer"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Kolom({ label, children }) {
  return (
    <div>
      <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">{label}</label>
      {children}
    </div>
  );
}
